import { Icon } from '@iconify/react'
import { useState, useMemo } from 'react'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import {
  useFetchESignFormQuery,
  useSubmitESignFormMutation,
  useFetchEncounterPhotosQuery,
  useFetchProductsQuery,
} from 'features/Procedure/state/procedure.api'
import { useParams } from 'react-router'
import { ESignModal } from '../e-sign-modal/ESignModal'
import { ESignLogModal } from '../e-sign-log-modal'
import {
  DEFAULT_ESIGN_COPY,
  resolveESignErrorMessage,
  resolveESignPinLength,
} from '../e-sign-modal/e-sign-modal.utils'
import styles from './e-sign-button.module.scss'
import classNames from 'classnames'
import { hasESignItems, TAB_TO_FORM_NAME } from './e-sign-status.utils'

const ESIGN_TABS = ['treatments', 'photos', 'soap', 'products', 'labs']

type Props = {
  activeTab: string
  patientId: number | null
  encounterUid: string | null
}

export const ESignButton = ({ activeTab, patientId, encounterUid }: Props) => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isLogModalVisible, setIsLogModalVisible] = useState(false)
  const { setNotification } = useNotification()

  const isESignTab = ESIGN_TABS.includes(activeTab)

  const { data: eSignStatus } = useFetchESignFormQuery(
    { patientId: patientId!, encounterUid: encounterUid! },
    { skip: !patientId || !encounterUid },
  )

  const { siteId } = useParams<{ siteId: string }>()

  const { data: photosData } = useFetchEncounterPhotosQuery(
    {
      siteId: siteId!,
      patientId: patientId!,
      encounterUid: encounterUid!,
    },
    { skip: !patientId || !encounterUid || !siteId || activeTab !== 'photos' },
  )

  const { data: productsData } = useFetchProductsQuery(
    {
      siteId,
      encounterUid: encounterUid,
    },
    { skip: !encounterUid || !siteId || activeTab !== 'products' },
  )

  const [submitESignForm, { isLoading: isSubmitting }] = useSubmitESignFormMutation()

  const formName = TAB_TO_FORM_NAME[activeTab]

  const signedData = useMemo(() => {
    if (!eSignStatus || !formName) return null
    const status = eSignStatus[formName]
    if (!status) return null
    return status.is_signed === 1 && status.items && status.items.length > 0 ? status : null
  }, [eSignStatus, formName])

  const isSigned = signedData !== null
  const hasSignHistory = hasESignItems(eSignStatus?.[formName])

  const hasFormToSign = useMemo(() => {
    if (!eSignStatus || !formName) return false
    const status = eSignStatus[formName]
    return status?.has_form_to_sign === 1
  }, [eSignStatus, formName])

  const handleOpenModal = () => {
    if (isSigned) {
      setIsLogModalVisible(true)
      return
    }

    if (activeTab === 'photos') {
      const hasPhotos = photosData && photosData.length > 0
      if (!hasPhotos) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Cannot Sign Photos',
          description: 'You cannot sign photos without any photos uploaded.',
        })
        return
      }
    }

    if (activeTab === 'products') {
      const hasProducts = productsData?.data?.items && productsData.data.items.length > 0
      if (!hasProducts) {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Cannot Sign Products',
          description: 'You cannot sign products without any products added.',
        })
        return
      }
    }

    setIsModalVisible(true)
  }

  const handleCloseModal = () => {
    setIsModalVisible(false)
  }

  const handleSubmit = async (pin: string): Promise<boolean> => {
    if (!patientId || !encounterUid || !formName) return false

    try {
      const result = await submitESignForm({
        patientId,
        encounterUid,
        payload: {
          form_name: formName,
          password: pin,
          amendment: '',
        },
      }).unwrap()

      if (result.success) {
        setIsModalVisible(false)
        return true
      } else {
        setNotification({
          type: ALERT_CONSTANTS.ERROR,
          duration: 2,
          title: 'Error',
          description: result.message || 'Failed to sign form',
        })
        return false
      }
    } catch (error: unknown) {
      const errorMessage = resolveESignErrorMessage(error, 'Failed to sign form')
      setNotification({
        type: ALERT_CONSTANTS.ERROR,
        duration: 2,
        title: 'Error',
        description: errorMessage,
      })
      return false
    }
  }

  if (!isESignTab || !patientId || !encounterUid || !hasFormToSign) return null

  return (
    <>
      <button
        type='button'
        className={classNames(styles.floatingButton, {
          [styles.floatingButtonHistory]: hasSignHistory,
        })}
        onClick={handleOpenModal}>
        <Icon icon='fa6-solid:signature' className={styles.signIcon} />
        eSign
      </button>
      <ESignModal
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
        description={DEFAULT_ESIGN_COPY}
        pinLength={resolveESignPinLength(eSignStatus)}
        signHistory={eSignStatus?.[formName]?.items}
        isLoading={isSubmitting}
      />
      <ESignLogModal
        isVisible={isLogModalVisible}
        onClose={() => setIsLogModalVisible(false)}
        signedData={signedData}
      />
    </>
  )
}
